import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Input from "components/Input";
import Modal from "components/Modal";
import PhoneNumber from "components/PhoneNumber";
import { IFactoringCompany, IPaymentSpeed } from "models/HaulPay";
import { connectFactoringCompanyAPI, updateDefaultPaymentTermAPI } from "services/api/joinHaulpay";
import { linkPlaidAccountAPI } from "services/api/userSetting";
import { showErrorMessage } from "helpers/toastMessage";
import { AppDispatch } from "store";
import { getPaymentTerms } from "store/user/actions";
import { selectOnboardingInformation, selectPaymentTerms } from "store/user/selector";
import NOAAttach from "./NOAAttach";
import SelectDefaultPaymentSpeed from "./SelectDefaultPaymentSpeed";
import SelectFactoringCompany from "./SelectFactoringCompany";
import styles from "./style.module.scss";

interface IProps {
  handleNext: VoidFunction;
  paymentProfileId: string;
}

export default function InvitedCarrierBankInfo({ handleNext, paymentProfileId }: Readonly<IProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const onboardingInformation = useSelector(selectOnboardingInformation);
  const paymentTerms = useSelector(selectPaymentTerms);
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState<IPaymentSpeed>();
  const [selectedFactoringCompany, setSelectedFactoringCompany] = useState<IFactoringCompany>();

  const [mode, setMode] = useState<"bank_attach" | "default" | "factoring_company" | "noa_attach">(
    "default"
  );
  const [isSubmittingFactor, setIsSubmittingFactor] = useState(false);

  const [isOpenModal, toggleModal] = useToggle();

  function confirmModal() {
    toggleModal();
  }

  const handlePlaidSuccess = async (publicToken: string, accounts: any[]) => {
    const requestBody = {
      public_token: publicToken,
      account_ids: accounts.map((account) => account.id)
    };
    await linkPlaidAccountAPI(requestBody);
    handleNext();
  };

  const handleChangePaymentTerm = async (id: string) => {
    setSelectedPaymentTerm(paymentTerms?.find((term) => term.id === id));
    await updateDefaultPaymentTermAPI(onboardingInformation.factoring_id, id, { is_default: true });
  };

  const handleChangeFactoringCompany = async (company?: IFactoringCompany) => {
    if (!company) return;

    try {
      setIsSubmittingFactor(true);
      await connectFactoringCompanyAPI(company.id, onboardingInformation.factoring_id);
      handleNext();
    } catch (error: any) {
      showErrorMessage(
        error?.response?.data?.detail || "Unable to submit the factoring company. Please try again."
      );
    } finally {
      setIsSubmittingFactor(false);
    }
  };

  useEffect(() => {
    if (!paymentTerms) dispatch(getPaymentTerms(onboardingInformation.factoring_id));
  }, []);

  useEffect(() => {
    if (paymentTerms) setSelectedPaymentTerm(paymentTerms.find((term) => term.is_default));
  }, [paymentTerms]);

  if (!paymentTerms || !selectedPaymentTerm) return null;

  return (
    <div className={styles.bankInfoContainer}>
      {mode === "default" ? (
        <SelectDefaultPaymentSpeed
          paymentTerms={paymentTerms}
          selectedPaymentTerm={selectedPaymentTerm}
          handleChangePaymentTerm={handleChangePaymentTerm}
          handlePlaidSuccess={handlePlaidSuccess}
          handleUseFactoringCompany={() => setMode("factoring_company")}
          handleBankNotListed={() => setMode("bank_attach")}
        />
      ) : (
        <>
          {mode === "factoring_company" ? (
            <SelectFactoringCompany
              factoringId={onboardingInformation.factoring_id}
              selectedFactoringCompany={selectedFactoringCompany}
              isSubmitting={isSubmittingFactor}
              handleCompanySelected={setSelectedFactoringCompany}
              handleClickNOA={() => setMode("noa_attach")}
              handleSubmitCompany={handleChangeFactoringCompany}
            />
          ) : (
            <NOAAttach
              paymentProfileId={paymentProfileId}
              purpose={mode === "noa_attach" ? "noa" : "bank"}
            />
          )}
        </>
      )}
      <div className={styles.footer}>
        <Button rounded className="!h-12 desktop:!w-64" onClick={handleNext}>
          NEXT
        </Button>
      </div>

      <Modal
        isOpen={isOpenModal}
        closeModal={toggleModal}
        wrapperClass="!my-auto text-sm"
        dialogClass="!bg-foreground !px-4 !py-4 !border !border-card !overflow-y-auto gap-2.5"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <span className="text-xl font-bold text-primary">New Factoring Company</span>
        <div className="mt-1 flex flex-col gap-4.5">
          <p className="text-sm">Please enter the information for your factoring company below.</p>

          <div className="flex flex-col gap-3.5">
            <Input label="&nbsp;&nbsp;DBA" />
            <Input type="email" label="&nbsp;&nbsp;Company Email" />
            <Input label="&nbsp;&nbsp;Company Address" />
            <PhoneNumber label="&nbsp;&nbsp;Company Phone" wrapperClass="flex-1" />
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-4 *:w-20 *:text-sm *:rounded-full *:h-9">
          <Button onClick={toggleModal} variant="outline">
            Cancel
          </Button>
          <Button onClick={confirmModal}>Confirm</Button>
        </div>
      </Modal>
    </div>
  );
}
